import { useEffect, useState } from "react";
import { MessageSquare, Bot, Handshake, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";
import { Rail } from "../components/Rail";
import api, { formatRp } from "../lib/api";

const LEVELS = [
  ["hot", "Hot", "Siap membeli, follow up sekarang", "bg-mint"],
  ["medium", "Medium", "Tertarik tapi masih mempertimbangkan", "bg-amber"],
  ["cold", "Cold", "Hanya lihat-lihat", "bg-slate"],
  ["unknown", "Belum Diketahui", "Belum cukup sinyal dari percakapan", "bg-white/20"],
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const exportCsv = async () => {
    try {
      const month = new Date().toISOString().slice(0, 7);
      const res = await api.get(`/analytics/export?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chatsensei-leads-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan CSV berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh laporan");
    }
  };

  const kpis = data && [
    { label: "Total Chat", value: data.total_chats, icon: MessageSquare, color: "text-mint" },
    { label: "Assistant", value: data.assistant, icon: Bot, color: "text-mint" },
    { label: "Handoff", value: data.handoff, icon: Handshake, color: "text-amber" },
    { label: "Estimasi Revenue", value: formatRp(data.estimated_revenue), icon: TrendingUp, color: "text-mint" },
  ];
  const distTotal = data ? Math.max(1, Object.values(data.interest_distribution).reduce((a, b) => a + b, 0)) : 1;
  const maxHour = data?.top_hours?.[0]?.count || 1;
  const maxProd = data?.top_products?.[0]?.count || 1;

  return (
    <div className="flex h-screen bg-ink text-paper">
      <Rail />
      <main className="flex-1 overflow-y-auto">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-white/5 bg-ink/80 px-8 py-5 backdrop-blur-md">
          <div>
            <h1 className="text-xl font-semibold">Analytics</h1>
            <p className="text-sm text-muted">Performa chat, 30 hari terakhir</p>
          </div>
          <button data-testid="analytics-export-btn" onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-muted transition-colors hover:border-white/25 hover:text-paper">
            <Download size={14} /> Export CSV
          </button>
        </header>
        <div className="space-y-6 p-8" data-testid="analytics-dashboard">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {(kpis || [1, 2, 3, 4].map(() => null)).map((k, i) =>
              k ? (
                <div key={k.label} data-testid={`kpi-card-${i}`} className="rounded-2xl border border-white/5 bg-panel p-4 shadow-card">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted">{k.label}</span>
                    <k.icon size={16} className={k.color} />
                  </div>
                  <p className="mt-3 font-mono text-2xl font-semibold">{k.value}</p>
                </div>
              ) : (
                <div key={i} className="h-24 animate-pulse rounded-2xl border border-white/5 bg-panel" />
              )
            )}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <section className="rounded-2xl border border-white/5 bg-panel p-6" data-testid="interest-distribution">
              <h2 className="text-base font-semibold md:text-lg">Distribusi Interest Level</h2>
              <div className="mt-5 space-y-5">
                {LEVELS.map(([key, label, desc, bar]) => {
                  const count = data?.interest_distribution?.[key] || 0;
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <span className={`h-2.5 w-2.5 rounded-full ${bar}`} />
                          <span className="font-medium">{label}</span>
                          <span className="text-xs text-faint">{desc}</span>
                        </span>
                        <span className="font-mono">{count}</span>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/5">
                        <div className={`h-full rounded-full ${bar}`} style={{ width: `${(count / distTotal) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="rounded-2xl border border-white/5 bg-panel p-6" data-testid="top-hours">
              <h2 className="text-base font-semibold md:text-lg">Jam Follow-up Terbaik</h2>
              <p className="text-xs text-faint">10 jam dengan volume pesan masuk tertinggi (WIB)</p>
              <div className="mt-5 space-y-2.5">
                {(data?.top_hours || []).map((h) => (
                  <div key={h.hour} className="flex items-center gap-3">
                    <span className="w-12 font-mono text-xs text-muted">{h.hour}</span>
                    <div className="h-5 flex-1 overflow-hidden rounded bg-white/5">
                      <div className="h-full rounded bg-mint/80" style={{ width: `${(h.count / maxHour) * 100}%` }} />
                    </div>
                    <span className="w-8 text-right font-mono text-xs">{h.count}</span>
                  </div>
                ))}
                {data && !data.top_hours.length && <p className="text-sm text-faint">Belum ada data pesan masuk.</p>}
              </div>
            </section>
          </div>

          <section className="rounded-2xl border border-white/5 bg-panel p-6" data-testid="top-products">
            <h2 className="text-base font-semibold md:text-lg">Produk Teratas</h2>
            <p className="text-xs text-faint">10 produk dengan jumlah chat terbanyak</p>
            <div className="mt-5 space-y-4">
              {(data?.top_products || []).map((p, i) => (
                <div key={p.code} className="flex items-center gap-4">
                  <span className="w-6 font-mono text-sm text-faint">{i + 1}</span>
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="h-10 w-10 rounded-lg object-cover" />
                  ) : (
                    <span className="h-10 w-10 rounded-lg bg-panel3" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium">{p.name}</p>
                      <span className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-faint">{p.code}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/5">
                      <div className="h-full rounded-full bg-mint" style={{ width: `${(p.count / maxProd) * 100}%` }} />
                    </div>
                  </div>
                  <span className="font-mono text-sm text-muted">{formatRp(p.price)}</span>
                  <span className="w-10 text-right font-mono text-sm">{p.count}</span>
                </div>
              ))}
              {data && !data.top_products.length && <p className="text-sm text-faint">Belum ada produk yang dibahas di chat.</p>}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
